// WebcamCapture — reusable component that requests the webcam, shows a
// live preview, and lets the parent grab a still JPEG with one call.
//
// Used by both IdUpload (capture ID via webcam) and SelfieCapture (live
// selfie). NOT used by the existing iCode webcam — that one is for video
// recording and managed separately by startStreaming() in iCode.jsx.
//
// Props:
//   onCapture(blob: Blob)   — called when user clicks "Capture"
//   facingMode?: string     — "user" (front-cam, default) or "environment" (rear)
//   width?: number          — preview width in px (default 480)
//   height?: number         — preview height in px (default 360)
//   mirror?: boolean        — flip horizontally for selfie view (default true for front cam)

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, RefreshCw, AlertTriangle, Loader2 } from 'lucide-react';

const DEFAULT_WIDTH = 480;
const DEFAULT_HEIGHT = 360;
const JPEG_QUALITY = 0.92;  // high quality — Rekognition is sensitive to compression

export default function WebcamCapture({
  onCapture,
  facingMode = 'user',
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  mirror = null,           // null = auto-detect from facingMode
  ctaLabel = 'Capture',
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus] = useState('idle'); // idle | starting | ready | error | capturing
  const [error, setError] = useState(null);

  // Auto-mirror for front cam (feels natural to the candidate), but pass
  // through an explicit `mirror` prop if the parent wants to override.
  const shouldMirror = mirror === null ? facingMode === 'user' : mirror;

  // ── Start the webcam stream on mount. Clean up on unmount. ─────────────
  useEffect(() => {
    let cancelled = false;
    setStatus('starting');
    setError(null);

    navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: facingMode },
        width:  { ideal: width  * 2 },   // request 2x for crisper still capture
        height: { ideal: height * 2 },
      },
      audio: false,
    }).then((stream) => {
      if (cancelled) {
        // Component unmounted before we got the stream — release it.
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Play promise rejection (e.g. autoplay block) is non-fatal —
        // the video element typically starts on user interaction.
        videoRef.current.play().catch(() => { /* ignore */ });
      }
      setStatus('ready');
    }).catch((err) => {
      if (cancelled) return;
      const msg = err?.message || String(err);
      // Friendlier messages for the two common failure modes.
      let friendly = msg;
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        friendly = 'Camera access denied. Please allow camera permissions and refresh.';
      } else if (err?.name === 'NotFoundError') {
        friendly = 'No camera detected on this device.';
      }
      setError(friendly);
      setStatus('error');
    });

    return () => {
      cancelled = true;
      const s = streamRef.current;
      if (s) {
        s.getTracks().forEach((t) => t.stop());
      }
      streamRef.current = null;
    };
  }, [facingMode, width, height]);

  // ── Snap a frame and pass it up as a JPEG blob ──────────────────────────
  const capture = useCallback(() => {
    if (status !== 'ready' || !videoRef.current || !streamRef.current) return;
    setStatus('capturing');
    try {
      const video = videoRef.current;
      // Use the actual video frame dimensions, not the preview size — we
      // want the highest-quality still we can get for Rekognition.
      const w = video.videoWidth || width;
      const h = video.videoHeight || height;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setError('Canvas not supported');
        setStatus('error');
        return;
      }
      // If the preview was mirrored we must UN-mirror in the captured
      // image — otherwise the ID photo would appear flipped.
      if (shouldMirror) {
        ctx.translate(w, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(video, 0, 0, w, h);

      // The callback is async so we can AWAIT the parent's onCapture
      // handler — typically an upload to /api/identity/upload-*. We stay
      // in the 'capturing' state for the full duration of the parent's
      // work, which is what hides the "Capture" button (prevents double-
      // click during upload). Previously we reset to 'ready' synchronously
      // right after firing onCapture, so a user could click capture again
      // mid-upload and trigger a second blob + second POST.
      canvas.toBlob(async (blob) => {
        if (!blob) {
          setError('Failed to capture image');
          setStatus('ready');
          return;
        }
        try {
          // Await whatever the parent returns. If onCapture is synchronous
          // (returns undefined), this is a no-op. If it's async (returns a
          // Promise), we wait for it before re-enabling the button.
          await onCapture(blob);
        } catch (_) {
          // Parent's handler threw — don't surface it here; the parent
          // is responsible for displaying its own error UI. We just want
          // to make sure the button comes back so the user can retry.
        } finally {
          setStatus('ready');
        }
      }, 'image/jpeg', JPEG_QUALITY);
    } catch (err) {
      setError(err?.message || 'Capture failed');
      setStatus('error');
    }
  }, [onCapture, shouldMirror, status, width, height]);

  // ── Manual retry — kicks the camera again if the user fixed permissions
  const retry = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStatus('starting');
    setError(null);
    // Trigger the effect by remounting via key change — simplest:
    window.location.reload();
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="rounded-lg overflow-hidden bg-black"
        style={{ width: `${width}px`, height: `${height}px` }}
      >
        {status === 'error' ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-white p-4 text-center">
            <AlertTriangle className="w-8 h-8 text-red-400 mb-2" />
            <div className="text-sm mb-3">{error}</div>
            <button
              type="button"
              onClick={retry}
              className="px-3 py-1.5 text-xs bg-white/10 hover:bg-white/20 rounded"
            >
              Retry
            </button>
          </div>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: shouldMirror ? 'scaleX(-1)' : undefined,
            }}
          />
        )}
      </div>

      {status === 'starting' && (
        <div className="flex items-center text-sm text-gray-500">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Starting camera…
        </div>
      )}

      {status === 'ready' && (
        <button
          type="button"
          onClick={capture}
          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
        >
          <Camera className="w-4 h-4 mr-2" />
          {ctaLabel}
        </button>
      )}

      {status === 'capturing' && (
        <div className="text-sm text-gray-500">
          <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
          Capturing…
        </div>
      )}
    </div>
  );
}
